import moment from "moment";
import "moment/locale/en-au";
import "moment/locale/zh-cn";
import "moment/locale/th";
import "moment/locale/vi";
import { isWeb } from "./platform";
import {
  LANDING_PAGE,
  PRINT_PREFERENCE,
  USER_INFO,
} from "../constants/storage-keys";
import { GetLocalStorage, SetLocalStorage } from "./cryptography";
import { ClinicMemberStatus, Gender, Modality } from "../constants/options";
import { UserInfo } from "../stores/user-store";
import { IsEmptyStr, IsZero } from "./field-validation";
import { CNDateTimeFormat } from "./date";
import {
  PACKAGE_END_DATA_NULL_PLACEHOLDER,
  PackageFeatures,
} from "./package-subscription";
import { DEFAULT_LANDING_PAGE } from "../constants/settings";
import useStores from "../hooks/use-stores";
import { t, use } from "i18next";
import { useIsDesktop } from "../hooks/use-is-desktop";
import { GetMiniUrl } from "./mini";
import { TimeSlotStyle } from "./profile";
import { FlowerIcon, GeneralModalityIcon } from "../images/prescription/svg-components";
import { MODALITY_OPTIONS } from "./prescription";
import { AiTargetLang } from "../constants/ai";

export const IS_SMALL_SCREEN = isWeb ? window.innerWidth <= 1024 : false;
export const IsDesktop = isWeb ? window.innerWidth > 1536 : false;
export const IsMiniDesktop = isWeb ? window.innerWidth >= 768 : false;
export const IsHTTPS = isWeb && window.location.protocol === "https:";

export const IsClinicOwner = (clinic: any, userId: number | string | null | undefined): boolean => {
  if (!clinic || userId == null) return false;
  const targetUserId = String(userId);
  return String(clinic.ownerId) === targetUserId
    || (Array.isArray(clinic.allOwners)
      && clinic.allOwners.some((owner: any) => owner != null && String(owner.ownerId) === targetUserId));
};

export enum Language {
  ENGLISH = "en-AU",
  TH_TH = "th-TH",
  VI_VN = "vi-VN",
  ZH_CN = "zh-CN",
}
export const LangList = [
  Language.ENGLISH,
  Language.TH_TH,
  Language.VI_VN,
  Language.ZH_CN,
];
export const GetLangLabel = (lang: Language) => {
  switch (lang) {
    case Language.ENGLISH:
      return "EN";
    case Language.TH_TH:
      return "ไทย";
    case Language.VI_VN:
      return "Tiếng Việt";
    case Language.ZH_CN:
      return "中文";
  }
};
export const GetLangKey = () => {
  if (isWeb && localStorage.LANG) return localStorage.LANG;
  return Language.ENGLISH;
};

// Resolves the current UI language to a backend-supported language code for
// use in `?lang=` API query params. Non-supported locales (TH/VI) fall back
// to English.
export const GetApiLang = (): Language => {
  const current = GetLangKey() as Language;
  // return API_SUPPORTED_LANGS.includes(current) ? current : Language.ENGLISH;
  return current;
};

// The "local" content language is the non-English language used alongside English
// in bilingual editors (e.g. PrescriptionTextEditor). English-system users keep
// Chinese for backward compatibility; other locales mirror their UI language.
export const GetPrimaryLocalLang = (lang?: Language): Language => {
  const current = lang ?? (GetLangKey() as Language);
  if (current === Language.TH_TH) return Language.TH_TH;
  if (current === Language.VI_VN) return Language.VI_VN;
  return Language.ZH_CN;
};

// API-side language name expected by the translate endpoint.
export const GetLangApiName = (
  lang: Language
): AiTargetLang => {
  switch (lang) {
    case Language.ENGLISH:
      return "English";
    case Language.TH_TH:
      return "Thai";
    case Language.VI_VN:
      return "Vietnamese";
    case Language.ZH_CN:
    default:
      return "Chinese";
  }
};

// i18n key for the full display name of a language (e.g. t('CHINESE') -> "中文").
export const GetLangI18nKey = (lang: Language): string => {
  switch (lang) {
    case Language.ENGLISH:
      return "ENGLISH";
    case Language.TH_TH:
      return "THAI";
    case Language.VI_VN:
      return "VIETNAMESE";
    case Language.ZH_CN:
    default:
      return "CHINESE";
  }
};

// Maps our app language codes to moment's locale codes so date tokens like
// MMM / ddd / dddd render in the active language.
const MOMENT_LOCALE_MAP: Record<string, string> = {
  [Language.ENGLISH]: "en-au",
  [Language.TH_TH]: "th",
  [Language.VI_VN]: "vi",
  [Language.ZH_CN]: "zh-cn",
};

// Force `a` / `A` format tokens to always emit English am/pm (lowercase / uppercase)
// regardless of active locale — otherwise zh-cn would render "下午", th its own
// localised meridiem, etc. `updateLocale` mutates and also switches to the target
// locale, so we snapshot and restore.
const englishMeridiem = (
  hour: number,
  _minute: number,
  isLower: boolean
): string => (isLower ? (hour < 12 ? "am" : "pm") : hour < 12 ? "AM" : "PM");

const previousLocale = moment.locale();
["zh-cn", "th", "vi"].forEach((code) => {
  moment.updateLocale(code, { meridiem: englishMeridiem });
});
moment.locale(previousLocale);

// Align moment's global locale with the current UI language. Safe to call
// repeatedly; pass a language to override the stored one (e.g. on toggle).
export const SyncMomentLocale = (lang?: Language) => {
  const current = lang ?? (GetLangKey() as Language);
  moment.locale(MOMENT_LOCALE_MAP[current] ?? "en-au");
};

// Run once on import so dates are localised from the very first render.
SyncMomentLocale();

export const IsAuthed = () => {
  return (
    isWeb && (localStorage.USER_TOKEN != null || localStorage.USER_INFO != null)
  );
};

export const GetUserProfile = () => {
  let userInfo: UserInfo = {
    id: 0,
    username: "",
    firstName: "",
    lastName: "",
    templateId: 0,
    gender: Gender.MALE,
    email: "",
    availableClinics: [],
    currentClinicId: 0,
    isPublished: false,
    profileImageSavedName: "",
    packagePermissions: {
      features: [PackageFeatures.BASIC],
      endDate: PACKAGE_END_DATA_NULL_PLACEHOLDER,
      clinicPackages: [],
      id: 0,
    },
    timeSlotStyle: TimeSlotStyle.DROPDOWN,
    entryPage: null,
  };
  if (isWeb && localStorage.USER_INFO) userInfo = GetLocalStorage(USER_INFO);
  return userInfo;
};

export const IsLocked = () => {
  const userInfo = GetUserProfile();
  return (
    !userInfo ||
    IsZero(userInfo.availableClinics?.length ?? 0) ||
    IsZero(userInfo.currentClinicId)
  );
};

export const GetErrorInfo = (err: any) => {
  //err is err:obj
  console.log(err);
  if (err?.isAuthError) {
    return "";
  }
  // Set by the Axios/FAxios response interceptors when a 403/600 has already
  // shown its one toast within the current burst (e.g. several clinic-scoped
  // calls firing together and all failing the same way) — every caller's own
  // `.catch((e) => rootStore.notify(GetErrorInfo(e), 'error'))` still runs,
  // but this suppresses every notification after the first.
  if (err?.suppressNotify) {
    return "";
  }

  if (err.message === "ERR_CANCELED") {
    // DO NOTHING, THIS IS A CANCELLED REQUEST
    return "";
  } else if (err.message.includes("Network error!")) {
    return "Failed to connect to server.";
  } else if (err.name === "AxiosError" && !err.response) {
    return "Failed to connect to server.";
  } else if (err.name === "AxiosError") {
    if (err.response.status === 404) return "404 Resource Not Found";
    else if (err.response.status === 400) return "Invalid request format";
    // Always this fixed message for a 403, regardless of whatever the backend's
    // own error text says — the RBAC gate that produced it already means
    // exactly this, and showing the raw backend message here is never more
    // useful than the fixed one.
    else if (err.response.status === 403) return t("NO_PERMISSION_ACCESS_FEATURE_OR_DATA");
    // Same fixed-message treatment as 403 — this clinic relationship is gone
    // (see network.ts/file-network.ts's 600 handling), so the raw backend
    // text is never more useful than this.
    else if (err.response.status === 600) return t("CURRENT_CLINIC_INVALID_MAY_HAVE_LEFT");
  } else if (err.name === "TypeError") {
    return `Error: ${err.message}`;
  } else if (!err.response || !err.response.data.message)
    return "Unknown error for VAI Server. Please contact the administrator.";
  let input = err.response.data.message;
  const regex = /Error:/g;
  let match: RegExpExecArray | null;
  let lastMatchIndex = -1;
  while ((match = regex.exec(input)) !== null) {
    lastMatchIndex = match.index;
  }
  if (lastMatchIndex !== -1) {
    return input.substring(lastMatchIndex + "Error:".length).trim();
  }
  return "Unknown error for VAI Server. Please contact the administrator.";
};

export const OnProcessObjList = (
  index: number,
  sourceList: any[],
  req?: any
): any[] => {
  let finalList = [...sourceList];
  if (index < 0) finalList.push(req);
  else finalList.splice(index, 1);
  return finalList;
};
export enum APILanguage {
  en = "en",
  cn = "cn",
}

export const ALPHABETS: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export const GetDeepCopy = (obj: any) => JSON.parse(JSON.stringify(obj));

export const IsWorkingInClinic = (clinicMemberStatus: ClinicMemberStatus) => {
  return (
    clinicMemberStatus == ClinicMemberStatus.APPROVED ||
    clinicMemberStatus == ClinicMemberStatus.PENDING_QUIT_APPROVAL
  );
};

export const NameConversion = (firstName: string, lastName: string) => {
  if (IsEmptyStr(firstName) || IsEmptyStr(lastName)) return "";
  return GetLangKey() === Language.ZH_CN
    ? `${lastName}${firstName}`
    : `${firstName} ${lastName}`;
};

/**
 * If the user is not linked to any clinic, these urls will be protected
 */
export const PROTECTED_URLS: string[] = [
  "/appointment",
  "/configuration",
  "/prescription",
];

export const DaysCountDown = (endTime: string) => {
  const now = moment().startOf("day");
  const end = moment(endTime, CNDateTimeFormat).startOf("day");
  const diff = end.diff(now, "days");
  return diff;
};

export const ReformatAuPhoneNum = (
  mobile: string,
  removeCountryCode: boolean = true
) => {
  if (!mobile) return mobile;
  const trimmed = mobile.trim();
  if (removeCountryCode) {
    // 如果是 +61 开头，去掉 +61，替换成 0
    if (trimmed.startsWith("+61")) {
      return "0" + trimmed.slice(3);
    }
    return trimmed;
  } else {
    if (trimmed.startsWith("0")) {
      return "+61" + trimmed.slice(1);
    }
    return trimmed;
  }
};

export const CutString = (a: string, n: number = 20): string => {
  if (a.length <= n) return a;
  const headLength = n - 5 - 1;
  if (headLength <= 0) return "…" + a.slice(-5);
  return a.slice(0, headLength) + "…" + a.slice(-5);
};

export const RollBackNameLanguage = (item: any) => PickLocalised(item, "name");

// Reads `${baseField}${Lang}` off `item` according to current UI language,
// e.g. PickLocalised(eva, "syndromeName") returns syndromeNameTh / syndromeNameVn
// / syndromeNameEn / syndromeNameCn depending on UI lang. Falls back through
// En → Cn so legacy data (without Th/Vn fields) still displays something useful.
export const PickLocalised = (item: any, baseField: string) => {
  const lang = GetLangKey();
  const en = item?.[`${baseField}En`] ?? "";
  const cn = item?.[`${baseField}Cn`] ?? "";
  if (lang == Language.ENGLISH) return en || cn;
  if (lang == Language.TH_TH) return (item?.[`${baseField}Th`] ?? "") || en || cn;
  if (lang == Language.VI_VN) return (item?.[`${baseField}Vn`] ?? "") || en || cn;
  return cn || en;
};

// Returns the backend field name that holds the user's primary "local" name
// for the current UI language. Used when read/writing option items so the
// content goes into the right field for the practitioner's locale:
//   TH UI → "nameTh", VN UI → "nameVn", ZH/EN UI → "nameCn".
// English UI keeps "nameCn" because the bilingual editor pattern stores
// English in `nameEn` and Chinese in `nameCn` as the historical default.
export const GetLocalNameField = (): "nameCn" | "nameTh" | "nameVn" => {
  switch (GetLangKey()) {
    case Language.TH_TH: return "nameTh";
    case Language.VI_VN: return "nameVn";
    default: return "nameCn";
  }
};

export const GetLandingPage = (isDesktop: boolean) => {
  return !isDesktop
    ? GetMiniUrl("/appointment")
    : GetLocalStorage(LANDING_PAGE) ?? DEFAULT_LANDING_PAGE;
};

export const IsEnglishChar = (char: string) => {
  return /^[A-Za-z]$/.test(char);
};

export const CopyToClipboard = (
  textToCopy: string,
  toastMessage: string = ""
) => {
  const { rootStore } = useStores();

  if (!isWeb) return false;
  if (!IsHTTPS) {
    rootStore.notify(t("FAILED_TO_COPY_TO_CLIPBOARD"), "error");
    return false;
  }

  if (!navigator.clipboard) {
    rootStore.notify(t("FAILED_TO_COPY_TO_CLIPBOARD"), "error");
    return false;
  }

  navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      if (toastMessage) rootStore.notify(toastMessage, "success");
      return true;
    })
    .catch(() => {
      rootStore.notify(t("FAILED_TO_COPY_TO_CLIPBOARD"), "error");
      return false;
    });
};

export const RoundNumberTo2Decimal = (num: number) => {
  return Math.round(num * 100) / 100;
  // return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const IsHttpsOrLocalhost = () => {
  if (typeof window === "undefined") return false;
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const isHttps = window.location.protocol === "https:";
  return isHttps || isLocalhost;
};

export const IsTwoListsIdentical = (
  listA: number[],
  listB: number[]
): boolean => {
  if (!listA || !listB) return false;
  if (listA.length !== listB.length) return false;

  const setA = new Set(listA);
  const setB = new Set(listB);

  if (setA.size !== setB.size) return false;

  for (let id of setA) {
    if (!setB.has(id)) return false;
  }

  return true;
};
export const IsClinicPackageIdentical = (a: any, b: any): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.clinicId !== b.clinicId) return false;
  if (!IsTwoListsIdentical(a.features, b.features)) return false;
  return true;
};

export const GetLocalPrintPreference = () => {
  let result = { useRaw: true, useGran: true };
  const local = GetLocalStorage(PRINT_PREFERENCE);
  if (local) {
    result = local;
  }
  return result;
};

export const SetLocalPrintPreference = (req: {
  useRaw: boolean;
  useGran: boolean;
}) => {
  SetLocalStorage(PRINT_PREFERENCE, req);
};


export const GetGenderLabel = (gender: Gender) => {
  if (gender == Gender.MALE) return t('MALE');
  if (gender == Gender.FEMALE) return t('FEMALE');
  if (gender == Gender.PREFER_NOT_TO_SAY) return t('PREFER_NOT_TO_SAY');
  return "-";
}

export const CompareVersions = (currentVersion: string, serverVersion: string) => {
  const a = currentVersion.split('.').map(Number);
  const b = serverVersion.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    if (a[i] > b[i]) return 1;
    if (a[i] < b[i]) return -1;
  }
  return 0;
}


export const SortModalityList = (modalityList: any[]) => {
  const getId = (item: any) => {
    return item.modalityId ?? item.id;
  }
  let tcmModalities = [Modality.CHINESE_MEDICINE];
  let highPriorityModalities = [Modality.ACUPUNCTURE, Modality.MASSAGE, Modality.REMEDIAL_MASSAGE];
  let dynamicList = modalityList.filter((item: any) => !IsZero(item.clinicId));
  let tcmList = modalityList.filter((item: any) => tcmModalities.includes(getId(item)));
  let highPriorityList = modalityList.filter((item: any) => highPriorityModalities.includes(getId(item)));
  let remainedList = modalityList.filter((item: any) => !highPriorityModalities.includes(getId(item)) && !tcmModalities.includes(getId(item)) && IsZero(item.clinicId))
    .sort((a: any, b: any) => PickLocalised(a, "name").localeCompare(PickLocalised(b, 'name')));
  return [...tcmList, ...dynamicList, ...highPriorityList, ...remainedList];
}

export const GetModalityIcon = (modality: any) => {
  const getId = (item: any) => {
    return item.modalityId ?? item.id;
  }
  return !modality || !IsZero(modality.clinicId) ? GeneralModalityIcon
    : MODALITY_OPTIONS.find((option: any) => option.id === getId(modality))?.icon ?? GeneralModalityIcon;
}

export const GetModalityName = (modality: any) => {
  const lang = GetLangKey();
  const getId = (item: any) => {
    return item?.modalityId ?? item?.id;
  }
  if (!modality) return "";
  if (lang == Language.ENGLISH) return modality.name;
  if (getId(modality) == Modality.REMEDIAL_MASSAGE) return t("REMEDIAL_MASSAGE_OPTION");
  return IsEmptyStr(PickLocalised(modality, "name")) ? modality?.name : PickLocalised(modality, "name");
}
